import { DataTable, Dialog, FormDatePicker, FormInput, FormPhoneInput, FormSectionHeader, FormSelect, Loader } from "@/components";
import { Button, Input } from "@/components/ui";
import { ADDRESS, ADDRESS_1, ADDRESS_2, ALTERNATE_LOGIN_ID, CITY, EMAIL_ID_1, EMAIL_ID_2, END_DATE, EXISTING, FIRST_NAME, INDIVIDUAL, LANDLINE_NO, LAST_NAME, MOBILE_NO, NEW, OCCUPANCY_TYPE, occupancyTypes, OWNER, OWNERSHIP_SHARE, PO_BOX, START_DATE, STATE, USER_ID, USER_TYPE, USERNAME, ZIPCODE } from "../consts";
import useAddEditDialog from "./useAddEditDialog";
import { AddEditDialogProps } from "./types";
import { FormProvider } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Search } from "lucide-react";

const AddEditDialog = ({
  open,
  setOpen,
  onSave,
  data,
  isEdit,
  ownershipType,
  ownerCount
}: AddEditDialogProps) => {
  const {keys,
    form,
    requiredFields,
    formData,
    stateList,
    isLoading,
    onSaveData,
    isUserDetailsLoading,
    isExisting,
    isDisableShare,
    columns,
    associatedCommunities,
    searchTerm,
    setSearchTerm,
    userList,
    onUpdateUserType,
    isValidateUserDataLoading
  } = useAddEditDialog({onSave, data, isEdit, ownershipType});
  const { errors } = form.formState;

  return (
    <Dialog
      open={open}
      setOpen={setOpen}
      title={isEdit ? "Edit User" : "Add New User"}
      contentClassName="lg:!max-w-[1200px] lg:!w-[1200px]"
      onSave={form.handleSubmit(onSaveData)}
      isLoading={isValidateUserDataLoading}
    >
      {isLoading ? <Loader /> :
        <FormProvider {...form}>
          <form>
            {isEdit ? <></> : 
              <>
                <div className="w-full">
                  <Input
                    type="text"
                    placeholder="Search by phone number"
                    value={searchTerm ?? ""}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full sm:w-72 bg-white h-[40px]"
                    startIcon={<Search className="w-4 h-4" />}
                  />
                </div>

                <div className="flex flex-wrap gap-3 mt-3">
                  {userList?.data?.length ? 
                    userList?.data?.map((d: any) => 
                    <Button variant="outline" type="button" onClick={() => onUpdateUserType(EXISTING, d?.userId?.toString())}>
                      {`${d?.firstName || ""} ${d?.lastName || ""}`.trim() || d?.username}
                    </Button>) :
                    <div className="text-[14px] border border-gray-300 rounded-md px-2 flex flex-row gap-3 w-full items-center">
                      No User found
                      <Button variant="link" type="button" onClick={() => onUpdateUserType(NEW)} className="p-0">
                        + Add New
                      </Button>
                    </div>
                  }
                </div>
              </>
            // <div className="border border-gray-300 rounded-sm p-4">
            //   <FormSectionHeader wrapperClassName="mb-2">User Type</FormSectionHeader>
            //   <div className={isExisting ? "mb-5" : ""}>
            //     <RadioGroup
            //       onValueChange={onUserTypeChange}
            //       value={(formData as any)[USER_TYPE]}
            //       className="flex flex-row gap-4 lg:col-span-2"
            //     >
            //       <div className="flex flex-row items-center gap-1">
            //         <RadioGroupItem
            //           value={EXISTING}
            //           id={EXISTING}
            //           className={isExisting ? "border-primary" : ""}
            //         />
            //         <Label htmlFor={EXISTING} className="font-normal">Existing User</Label>
            //       </div>
            //       <div className="flex flex-row items-center gap-1">
            //         <RadioGroupItem
            //           value={NEW}
            //           id={NEW}
            //           className={(formData as any)[USER_TYPE] == NEW ? "border-primary" : ""}
            //         />
            //         <Label htmlFor={NEW} className="font-normal">New User</Label>
            //       </div>
            //     </RadioGroup>
            //   </div>
            //   {isExisting ? <div className="w-[250px]">
            //     <FormSelect
            //       name={`${USER_ID}`}
            //       control={form.control}
            //       label="User List"
            //       placeholder="Please select"
            //       isRequired={isExisting}
            //       options={userOptions}
            //       isLoadingMore={isUsrListLoading}
            //       hasMore={hasMore}
            //       onLoadMore={loadMoreOptions}
            //       onSearchChange={onSearchChange}
            //     />
            //   </div> : <></>}
            // </div>
            }
            <>
              {(formData as any)[USER_TYPE] == NEW || (formData as any)[USER_ID] ?
                isUserDetailsLoading ? <Loader / > : 
                <>
                  <div className={cn(isEdit ? "" : "mt-[20px]", "border border-gray-300 rounded-sm p-4")}>
                    <div>
                      <FormSectionHeader>User Info</FormSectionHeader>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                        <FormInput
                          name={`${USERNAME}`}
                          control={form.control}
                          label="User ID"
                          placeholder="Please enter"
                          error={(errors as any)[`${USERNAME}`]?.message}
                          isRequired={requiredFields.includes(`${USERNAME}`)}
                          noSpace
                          disabled={isExisting}
                        />
                        <FormInput
                          name={`${ALTERNATE_LOGIN_ID}`}
                          control={form.control}
                          label="Alternative Login ID"
                          placeholder="Please enter"
                          error={(errors as any)[`${ALTERNATE_LOGIN_ID}`]?.message}
                          isRequired={requiredFields.includes(`${ALTERNATE_LOGIN_ID}`)}
                          noSpace
                          disabled={isExisting}
                        />
                        <FormInput
                          name={`${FIRST_NAME}`}
                          control={form.control}
                          label="First Name"
                          placeholder="Please enter"
                          error={(errors as any)[`${FIRST_NAME}`]?.message}
                          isRequired={requiredFields.includes(`${FIRST_NAME}`)}
                          disabled={isExisting}
                        />
                        <FormInput
                          name={`${LAST_NAME}`}
                          control={form.control}
                          label="Last Name"
                          placeholder="Please enter"
                          error={(errors as any)[`${LAST_NAME}`]?.message}
                          isRequired={requiredFields.includes(`${LAST_NAME}`)}
                          disabled={isExisting}
                        />
                        <FormPhoneInput
                          name={`${MOBILE_NO}`}
                          control={form.control}
                          label="Mobile No"
                          placeholder="Please enter"
                          error={(errors as any)[`${MOBILE_NO}`]?.message}
                          isRequired={requiredFields.includes(`${MOBILE_NO}`)}
                          disabled={isExisting}
                        />
                        <FormPhoneInput
                          name={`${LANDLINE_NO}`}
                          control={form.control}
                          label="Landline No"
                          placeholder="Please enter"
                          error={(errors as any)[`${LANDLINE_NO}`]?.message}
                          isRequired={requiredFields.includes(`${LANDLINE_NO}`)}
                          disabled={isExisting}
                        />
                        <FormInput
                          name={`${EMAIL_ID_1}`}
                          control={form.control}
                          label="Email ID 1"
                          placeholder="Please enter"
                          error={(errors as any)[`${EMAIL_ID_1}`]?.message}
                          isRequired={requiredFields.includes(`${EMAIL_ID_1}`)}
                          disabled={isExisting}
                        />
                        <FormInput
                          name={`${EMAIL_ID_2}`}
                          control={form.control}
                          label="Email ID 2"
                          placeholder="Please enter"
                          error={(errors as any)[`${EMAIL_ID_2}`]?.message}
                          isRequired={requiredFields.includes(`${EMAIL_ID_2}`)}
                          disabled={isExisting}
                        />
                      </div>
                    </div>
                    <div className="mt-8">
                      <FormSectionHeader>Address</FormSectionHeader>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                        <FormInput
                          name={`${ADDRESS}.${ADDRESS_1}`}
                          control={form.control}
                          label="Address 1"
                          placeholder="Please enter"
                          error={(errors as any)[`${ADDRESS}`]?.[`${ADDRESS_1}`]?.message}
                          isRequired={requiredFields.includes(`${ADDRESS}.${ADDRESS_1}`)}
                          disabled={isExisting}
                        />
                        <FormInput
                          name={`${ADDRESS}.${ADDRESS_2}`}
                          control={form.control}
                          label="Address 2"
                          placeholder="Please enter"
                          error={(errors as any)[`${ADDRESS}`]?.[`${ADDRESS_2}`]?.message}
                          isRequired={requiredFields.includes(`${ADDRESS}.${ADDRESS_2}`)}
                          disabled={isExisting}
                        />
                        <FormSelect
                          key={keys.state}
                          name={`${ADDRESS}.${STATE}`}
                          control={form.control}
                          label="State"
                          placeholder="Please select"
                          error={(errors as any)[`${ADDRESS}`]?.[`${STATE}`]?.message}
                          isRequired={requiredFields.includes(`${ADDRESS}.${STATE}`)}
                          options={stateList?.map((data: any) => ({
                            label: data.name,
                            value: data.code,
                          }))}
                          disabled={isExisting}
                        />
                        <FormInput
                          name={`${ADDRESS}.${CITY}`}
                          control={form.control}
                          label="City"
                          placeholder="Please select"
                          error={(errors as any)[`${ADDRESS}`]?.[`${CITY}`]?.message}
                          isRequired={requiredFields.includes(`${ADDRESS}.${CITY}`)}
                          variant="char"
                          disabled={isExisting}
                        />
                        <FormInput
                          name={`${ADDRESS}.${ZIPCODE}`}
                          control={form.control}
                          label="Zipcode"
                          placeholder="Please enter"
                          error={(errors as any)[`${ADDRESS}`]?.[`${ZIPCODE}`]?.message}
                          isRequired={requiredFields.includes(`${ADDRESS}.${ZIPCODE}`)}
                          maxLength={10}
                          variant="integerSpeChar"
                          disabled={isExisting}
                        />
                        <FormInput
                          name={`${ADDRESS}.${PO_BOX}`}
                          control={form.control}
                          label="PO Box"
                          placeholder="Please enter"
                          error={(errors as any)[`${ADDRESS}`]?.[`${PO_BOX}`]?.message}
                          isRequired={requiredFields.includes(`${ADDRESS}.${PO_BOX}`)}
                          minLength={3}
                          maxLength={3}
                          variant="integer"
                          disabled={isExisting}
                        />
                      </div>
                    </div>
                    <div className="mt-8">
                      <FormSectionHeader>Occupancy Type</FormSectionHeader>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                        <FormSelect
                          key={keys.type}
                          name={`${OCCUPANCY_TYPE}`}
                          control={form.control}
                          label="Type"
                          placeholder="Please Select"
                          error={(errors as any)[`${OCCUPANCY_TYPE}`]?.message}
                          isRequired={requiredFields.includes(OCCUPANCY_TYPE)}
                          options={occupancyTypes.map((ocp: any) => ({...ocp, disabled: ocp.value == OWNER && ownershipType == INDIVIDUAL && ownerCount == 1}))}
                          disabled={formData[END_DATE]}
                        />
                        {formData[OCCUPANCY_TYPE] == OWNER ?
                          <FormInput
                            name={`${OWNERSHIP_SHARE}`}
                            control={form.control}
                            label="Ownership Share"
                            placeholder="Please enter"
                            isRequired={requiredFields.includes(`${OWNERSHIP_SHARE}`)}
                            variant="integer"
                            disabled={isDisableShare}
                          /> : <></>}
                        <FormDatePicker
                          name={`${START_DATE}`}
                          control={form.control}
                          label="Start Date"
                          placeholder="Please select"
                          error={(errors as any)[`${START_DATE}`]?.message}
                          isRequired={true}
                          disabled={formData[END_DATE]}
                        />
                        <FormDatePicker
                          name={`${END_DATE}`}
                          control={form.control}
                          label="End Date"
                          placeholder="Please select"
                          error={(errors as any)[`${END_DATE}`]?.message}
                          isRequired={requiredFields.includes(`${END_DATE}`)}
                          disabled={
                            formData[END_DATE] || !formData[START_DATE]
                          }
                          minDate={new Date((formData as any)[START_DATE])}
                        />
                      </div>
                    </div>
                    <div className="mt-8">
                      <FormSectionHeader>Associated Communities</FormSectionHeader>
                      <div>
                        <DataTable
                          data={associatedCommunities || []}
                          columns={columns}
                          className="shadow-none"
                        />
                      </div>
                    </div>
                  </div>
                </> : <></>
              }
            </>
          </form>
        </FormProvider>
      }
    </Dialog>
  )
}

export default AddEditDialog;
